import { KeyboardEvent, ReactNode } from 'react';

import styled from 'styled-components';

import { IconJourneyItem } from '@components/icons/journey-icon';
import { ListCard } from '@components/list-card';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { journeyListStrings } from './list-strings';

export type JourneyCardProps = {
  title: string;
  description?: string | null;
  fieldsCount?: number;
  preFilledCount?: number;
  /** Overrides the computed "{n} fields · {m} pre-filled" line with a custom summary. */
  countsText?: string;
  isMuted?: boolean;
  /** Icon-tile colour pair. 'default' is the current (blue) colours used by
   * every existing caller; callers that switch onto this shared card from a
   * differently-toned local card (e.g. the add-to-journey sidebar) pass
   * 'blue' explicitly. */
  iconTone?: 'default' | 'blue';
  actions?: ReactNode;
  dataCy?: string;
  /**
   * Makes the whole row a real interactive element (G46/G47): ListCard's own
   * `interactive` flag is derived from `Boolean(onClick)`, which is what
   * turns on Card's hover-background CSS — passing the same handler through
   * also as `role="button"` + `tabIndex` + a keydown handler is what makes it
   * keyboard-activatable, since ListCard/Card provide the hover affordance
   * but not keyboard semantics. Omitted entirely, the row renders exactly as
   * before (a plain, non-interactive summary).
   */
  onClick?: () => void;
};

export const JourneyCard = ({
  title,
  description,
  fieldsCount,
  preFilledCount,
  countsText,
  isMuted = false,
  iconTone = 'default',
  actions,
  dataCy,
  onClick,
}: JourneyCardProps) => {
  const { t } = useScopedTranslate(locale);

  // No plural machinery in @locale/translate — branch explicitly on
  // fieldsCount === 1, the same pattern add-fields-modal.tsx uses for its own
  // singular/plural button text. Every current caller passes fieldsCount and
  // preFilledCount together or not at all, so the combined summary only
  // renders when both are present.
  const countsLine =
    countsText ??
    (fieldsCount !== undefined && preFilledCount !== undefined
      ? t(fieldsCount === 1 ? 'fieldsSummaryOne' : 'fieldsSummaryMany', {
          fieldsCount: String(fieldsCount),
          preFilledCount: String(preFilledCount),
        })
      : null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // ' ' would otherwise scroll the page, same as any other button.
    event.preventDefault();
    onClick?.();
  };

  return (
    <div data-cy={dataCy}>
      <ListCard
        spaceX={3}
        spaceY={3}
        icon={
          <IconTile $isMuted={isMuted} $tone={iconTone}>
            <IconJourneyItem size={20} color={isMuted ? 'textHelper' : 'blue100'} />
          </IconTile>
        }
        title={title}
        description={description}
        descriptionFont="regular12"
        details={
          countsLine !== null ? (
            <Text font="regular12" color="textHelper" text={countsLine} />
          ) : undefined
        }
        actions={actions}
        onClick={onClick}
        {...(onClick ? { role: 'button' as const, tabIndex: 0, onKeyDown: handleKeyDown } : {})}
      />
    </div>
  );
};

// Both non-muted tones currently render the same tile pair — 'blue' exists so
// a caller switching onto this shared card from a differently-toned local
// card (add-to-journey-sidebar's IconContainer was purple) can say so
// explicitly, rather than silently inheriting whatever 'default' happens to
// mean. Muted and non-muted render identically: blue10/blue20 have no
// dark-mode values, unlike uiTrans5/border10, which both do.
const IconTile = styled.div<{ $isMuted: boolean; $tone: 'default' | 'blue' }>(() => ({
  width: pxToRem(40),
  height: pxToRem(40),
  borderRadius: pxToRem(8),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: theme.color.uiTrans5,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
}));

const locale = {
  en: { ...journeyListStrings.en },
  is: { ...journeyListStrings.is },
};
